/**
 * Native personalizer v2: shared on-product preview overlay math.
 *
 * SINGLE SOURCE OF TRUTH for how the embroidery preview lays text onto a ghost
 * photo. The theme renderer (assets/native-personalizer.js) and the calibration
 * tool (docs/tools/calibrate-preview.html) both run THIS code, so a calibrated
 * placement renders for the customer exactly as it did in the tool. The
 * calibrator vendors a byte-identical copy between its BEGIN/END VENDORED
 * markers: after editing this file, re-copy the whole file between those
 * markers (the S9 byte-compare check enforces it).
 *
 * Pure functions only: no DOM, no fetch, no theme globals. Placement spec
 * (BUILD.md Phase 6.1): {ghost, box: {x, y, w}, arc, rot} where x/y = box
 * centre and w = box width, all relative (0-1) to the ghost image dims; arc =
 * integer slider value; rot = degrees.
 *
 * Per-type placements (2026-10-12): custom.preview_placement may carry a sub-object per type,
 * {"standard": {...}, "initials": {ghost, box, rot}, "surname": {...}}; the top-level
 * ghost/box (legacy) still drives Standard when there is no `standard` sub-object. The initials
 * box places the stacked "I heart" mark + initials block (initialsLayout / initialsBoxToPixels).
 *
 * MRS. Surname lockup (2026-10-10): the same spec nested under a `surname` key
 * of custom.preview_placement ({"surname": {ghost, box, arc, rot}}). The box
 * and arc place the SURNAME line exactly as a standard placement places its
 * text; the fixed "MRS." line is laid out above it by lockupMetrics() /
 * mrsPathD() below.
 */
(function () {
  'use strict';

  // Embroidery-like sizing: constant letter height up to MIN_FIT_CHARS, then
  // shrink to fit the box (3 vs 18 chars = ~1.8x, not ~4.5x). The studio's real
  // stitched sizing rule is still owed (BUILD.md Phase 6.3); when it lands, the
  // model changes HERE only.
  var MIN_FIT_CHARS = 10;

  // Average glyph width as a fraction of font-size, per config font class.
  // Deliberately rough: exactFitFontSize corrects with a real glyph-run
  // measurement, because the estimate under-measures wide runs (all caps) and
  // SVG silently clips glyphs that overrun the path.
  var FACE_WIDTH_FACTORS = { script: 0.52, serif: 0.62 };
  var DEFAULT_WIDTH_FACTOR = 0.62;

  var EDGE_PAD_PX = 4; // path inset from the box edges
  var BOX_MIN_H_PX = 34; // height floor so narrow boxes keep a usable line
  var BOX_H_RATIO = 0.34; // box height = 34% of its width

  // MRS. Surname lockup proportions (TUNABLES). The studio stitches a fixed art
  // file: "MRS." in small serif capitals centred above the surname in large
  // script. Tuned to Lex's reference photos 2026-10-10 (MRS. caps sit right on
  // the surname, tight tracking); the studio art file is still to confirm. All are fractions of the SURNAME font size, so the
  // lockup scales as one unit when the surname shrinks to fit the box.
  var MRS_CAP_HEIGHT_RATIO = 0.42; // MRS. cap height = 0.42 x surname font size
  var MRS_GAP_RATIO = 0.06; // gap between the surname's top and the MRS. baseline
  var SURNAME_LETTER_HEIGHT_RATIO = 0.55; // visual height of the surname's letters above its baseline
  var SERIF_CAP_HEIGHT_EM = 0.662; // Times New Roman cap height / em, converts a cap height to a font-size
  var MRS_LETTER_SPACING_EM = 0; // MRS. tracking, as a fraction of the MRS. font size (normal: tightened from 0.04 per team review 2026-10-12)

  // Initials ghost (2026-10-12): the SAME artwork and layout as the initials text card
  // (native-personalizer.css `.np-typewriter`): the "I heart" mark (165x108 symbol, fixed colours)
  // stacked over the typed initials in the typewriter face. All sizes are the card's own em
  // proportions (mark 2.2em wide, initials 1.75em, 0.1em gap), so the ghost is that card scaled so the
  // MARK is exactly the placement box's width. There is no thread colour: Initials has none, the
  // heart keeps its artwork colours and the text uses the theme ink, exactly like the card.
  var INITIALS_MARK_ASPECT = 165 / 108; // the symbol's viewBox
  var INITIALS_MARK_EM = 2.2; // mark width, in card ems
  var INITIALS_TEXT_EM = 1.75; // initials font-size, in card ems
  var INITIALS_LINE_EM = 1.1; // initials line box, in initials font-sizes
  var INITIALS_GAP_EM = 0.1; // gap between the mark and the initials, in card ems
  var INITIALS_BASELINE_EM = 0.85; // initials baseline below its line-box top, in initials font-sizes (tunable)
  // GHOST ONLY (the initials TEXT CARD is untouched): Carlin 2026-10-12 asked for the initials a bit closer to
  // the I-heart. The VISIBLE gap is mark bottom to initials cap-top = the em gap plus the empty space between
  // the line-box top and the cap top. INITIALS_GAP_TIGHTEN is the fraction of that visible gap removed (0.4 =
  // 40% tighter), a tunable; the cap height only locates the cap top (typewriter caps ~0.7 em).
  var INITIALS_CAP_HEIGHT_EM = 0.7;
  var INITIALS_GAP_TIGHTEN = 0.4;
  // Crop-window framing for the initials figure (2026-10-12). The crop zoom is derived from the
  // placement box's width, and an initials box is small (w ~0.1 against ~0.3 for a text line), so
  // deriving it from the initials box over-zooms (~1.9x against the other branches on the same garment).
  // Floor for the width the zoom is derived from when the garment has no other placement to match:
  var INITIALS_CROP_MIN_BOX_W = 0.3;
  // The card lets the initials run wider than the mark (2 wide typewriter glyphs ~ the mark's width); the
  // fit pass only stops a pathological run, at this multiple of the mark width.
  var INITIALS_TEXT_MAX_WIDTH_RATIO = 1.3;

  var CENTER_SNAP = 0.015; // magnetic-centerline threshold (relative x)
  var ROT_SNAP_TARGETS = [-90, 0, 90]; // upright + both verticals
  var ROT_SNAP_TOLERANCE = 4; // degrees

  // Pale thread on a white ghost needs a faint stroke to stay legible without
  // lying about the colour (BUILD.md Phase 6.4 renderer visibility rule). Keyed
  // on luminance, not a hex allowlist, so White AND near-whites (Buttercream)
  // get the treatment and a future ivory thread inherits it for free.
  var PALE_THREAD_LUMINANCE = 0.82; // White = 1.0, Buttercream #fff3a3 = 0.88, Pink #fcd3d9 = 0.72
  var PALE_THREAD_STROKE = 'paint-order:stroke;stroke:#d0c9bd;stroke-width:0.6px';

  // Validate + normalise a placement (object or JSON string, numbers may
  // arrive as data-attribute strings). Returns {ghost, box:{x,y,w}, arc, rot}
  // or null. Callers FAIL OPEN on null: the v1 styled-text input renders and
  // no preview appears. The preview is progressive enhancement, the opposite
  // of the config guard (which fails closed).
  function parsePlacement(raw) {
    var p = raw;
    if (typeof raw === 'string') {
      try {
        p = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (!p || typeof p !== 'object' || !p.box || typeof p.ghost !== 'string' || !p.ghost) return null;
    var x = Number(p.box.x);
    var y = Number(p.box.y);
    var w = Number(p.box.w);
    // Non-finite arc/rot (e.g. "Infinity") would emit invalid SVG path data and
    // rotate(Infinitydeg); treat them as absent rather than poisoning the render.
    var arc = Number(p.arc);
    var rot = Number(p.rot);
    if (!isFinite(arc)) arc = 0;
    if (!isFinite(rot)) rot = 0;
    if (!isFinite(x) || !isFinite(y) || !isFinite(w) || w <= 0) return null;
    return { ghost: p.ghost, box: { x: x, y: y, w: w }, arc: arc, rot: rot };
  }

  // Relative placement box to a pixel rect inside the rendered container.
  function boxToPixels(box, containerW, containerH) {
    var w = box.w * containerW;
    var h = Math.max(BOX_MIN_H_PX, w * BOX_H_RATIO);
    return { w: w, h: h, left: box.x * containerW - w / 2, top: box.y * containerH - h / 2 };
  }

  // Baseline height + sag of the text path (shared by pathD and mrsPathD so the MRS. line can
  // never drift off the surname's curve).
  function baselineParts(hPx, arc) {
    var sag = (arc / 40) * (hPx * 0.9);
    var y = hPx / 2 + Math.max(0, -sag * 0.25) + hPx * 0.18;
    return { sag: sag, y: y };
  }

  // Text path across the box: flat baseline at arc 0, quadratic sag/rise otherwise.
  function pathD(wPx, hPx, arc) {
    var parts = baselineParts(hPx, arc);
    var sag = parts.sag;
    var y = parts.y;
    return 'M' + EDGE_PAD_PX + ' ' + y + ' Q ' + wPx / 2 + ' ' + (y - sag * 2) + ' ' + (wPx - EDGE_PAD_PX) + ' ' + y;
  }

  // MRS. Surname lockup sizes, from the FINAL (post exact-fit) surname font size. Returns the
  // MRS. font-size, how far above the surname's baseline the MRS. baseline sits (px), and the
  // MRS. letter-spacing in em. Pure so the renderer and the calibrator draw the same lockup.
  function lockupMetrics(surnameFontSize) {
    var fs = Number(surnameFontSize);
    if (!isFinite(fs) || fs < 0) fs = 0;
    return {
      mrsFontSize: (fs * MRS_CAP_HEIGHT_RATIO) / SERIF_CAP_HEIGHT_EM,
      mrsLiftPx: fs * (SURNAME_LETTER_HEIGHT_RATIO + MRS_GAP_RATIO),
      mrsLetterSpacingEm: MRS_LETTER_SPACING_EM
    };
  }

  // The surname's path lifted straight up by liftPx: the MRS. line follows the same arc, centred
  // above the surname at every arc setting.
  function mrsPathD(wPx, hPx, arc, liftPx) {
    var parts = baselineParts(hPx, arc);
    var lift = Number(liftPx);
    if (!isFinite(lift)) lift = 0;
    var y = parts.y - lift;
    return 'M' + EDGE_PAD_PX + ' ' + y + ' Q ' + wPx / 2 + ' ' + (y - parts.sag * 2) + ' ' + (wPx - EDGE_PAD_PX) + ' ' + y;
  }

  // Initials ghost layout for a block whose MARK is boxW px wide (pure). Returns the mark rect and
  // the initials font-size / baseline, all in px inside a block of totalH px, centred on x = boxW/2.
  function initialsLayout(boxW) {
    var w = Number(boxW);
    if (!isFinite(w) || w < 0) w = 0;
    var em = w / INITIALS_MARK_EM;
    var markH = w / INITIALS_MARK_ASPECT;
    var gap = em * INITIALS_GAP_EM;
    var textFs = em * INITIALS_TEXT_EM;
    var lineH = textFs * INITIALS_LINE_EM;
    // The visible gap (mark bottom to initials cap-top) before tightening, and the px removed from it. The
    // block's total height shrinks by the same amount, and the renderer centres the block on the placement
    // box, so the combined mark + initials stays centred where it was (the centre does not drift).
    var visibleGap = gap + textFs * (INITIALS_BASELINE_EM - INITIALS_CAP_HEIGHT_EM);
    var tighten = visibleGap * INITIALS_GAP_TIGHTEN;
    return {
      markW: w,
      markH: markH,
      textFs: textFs,
      textY: markH + gap + textFs * INITIALS_BASELINE_EM - tighten,
      totalH: markH + gap + lineH - tighten,
      visibleGap: visibleGap - tighten,
      untightenedGap: visibleGap
    };
  }

  // The box width the initials figure derives its crop-window zoom from. Initials must frame the garment
  // like the product's OTHER ghosts: pass the reference widths in priority order (the Standard
  // placement's box.w, then the surname's) and the first usable one wins, so the zoom equals that
  // ghost's (the renderer feeds it to the same formula; centring stays on the initials box). With no
  // reference, max(initialsW, INITIALS_CROP_MIN_BOX_W) stops a small design from over-zooming. Pure.
  function initialsCropBoxW(initialsW, referenceWs) {
    var list = referenceWs || [];
    for (var i = 0; i < list.length; i++) {
      var w = Number(list[i]);
      if (isFinite(w) && w > 0) return w;
    }
    var own = Number(initialsW);
    if (!isFinite(own) || own <= 0) own = INITIALS_CROP_MIN_BOX_W;
    return Math.max(own, INITIALS_CROP_MIN_BOX_W);
  }

  // Relative initials placement box (x/y = block CENTRE, w = mark width) to a pixel rect, like
  // boxToPixels but with the block's own height instead of a text-line height.
  function initialsBoxToPixels(box, containerW, containerH) {
    var w = box.w * containerW;
    var layout = initialsLayout(w);
    return { w: w, h: layout.totalH, left: box.x * containerW - w / 2, top: box.y * containerH - layout.totalH / 2, layout: layout };
  }

  // Inline style for the MRS. overlay <text>: its font-size, tracking, and the same thread fill +
  // pale-thread stroke treatment as the surname (so the two lines read as one stitched lockup).
  function mrsTextStyle(mrsFontSize, hex) {
    return textStyle(mrsFontSize, hex) + ';letter-spacing:' + MRS_LETTER_SPACING_EM + 'em';
  }

  // First-pass font size from the per-character width estimate.
  function fontSizePx(wPx, hPx, textLength, faceClass) {
    var factor = FACE_WIDTH_FACTORS[faceClass] || DEFAULT_WIDTH_FACTOR;
    var perChar = (wPx - 2 * EDGE_PAD_PX) / Math.max(MIN_FIT_CHARS, textLength);
    return Math.min(hPx * 0.62, perChar / factor);
  }

  // Exact-fit pass: given the REAL path length and the REAL measured glyph run
  // at fontSize, shrink so the run fits the path. A degenerate path (room <= 0,
  // e.g. a hand-authored near-zero box.w) must not produce a negative size.
  function exactFitFontSize(fontSize, pathTotalLength, runLength) {
    var room = pathTotalLength - EDGE_PAD_PX;
    if (room > 0 && runLength > room) return (fontSize * room) / runLength;
    return fontSize;
  }

  // Magnetic centerline while dragging: snap x to 0.5 inside the threshold.
  // `snapped` drives the calibrator's guide line.
  function snapCenterX(x) {
    if (Math.abs(x - 0.5) < CENTER_SNAP) return { x: 0.5, snapped: true };
    return { x: x, snapped: false };
  }

  // Rotation snaps at upright and both verticals (accessories embroider
  // vertically, e.g. hair-bow ribbon tails).
  function snapRotation(deg) {
    for (var i = 0; i < ROT_SNAP_TARGETS.length; i++) {
      if (Math.abs(deg - ROT_SNAP_TARGETS[i]) <= ROT_SNAP_TOLERANCE) return ROT_SNAP_TARGETS[i];
    }
    return deg;
  }

  // Drag/resize clamps: keep the box on the photo, keep it grabbable.
  function clampX(x) {
    return Math.min(0.98, Math.max(0.02, x));
  }
  function clampY(y) {
    return Math.min(0.98, Math.max(0.02, y));
  }
  function clampW(w) {
    return Math.min(0.9, Math.max(0.12, w));
  }

  // WCAG relative luminance of a #rrggbb hex; -1 when unparseable (no stroke).
  function relativeLuminance(hex) {
    if (typeof hex !== 'string' || !/^#[0-9a-f]{6}$/i.test(hex)) return -1;
    var lin = [0, 0, 0];
    for (var i = 0; i < 3; i++) {
      var c = parseInt(hex.substr(1 + i * 2, 2), 16) / 255;
      lin[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  }

  // Inline style for the overlay <text>: size + thread fill + the pale-thread
  // stroke treatment. Font-family comes from a CSS face class, not from here.
  function textStyle(fontSize, hex) {
    var s = 'font-size:' + fontSize + 'px;fill:' + hex;
    if (relativeLuminance(hex) >= PALE_THREAD_LUMINANCE) s += ';' + PALE_THREAD_STROKE;
    return s;
  }

  window.ALTPreviewMath = {
    MIN_FIT_CHARS: MIN_FIT_CHARS,
    EDGE_PAD_PX: EDGE_PAD_PX,
    CENTER_SNAP: CENTER_SNAP,
    INITIALS_CROP_MIN_BOX_W: INITIALS_CROP_MIN_BOX_W,
    INITIALS_GAP_TIGHTEN: INITIALS_GAP_TIGHTEN,
    INITIALS_CAP_HEIGHT_EM: INITIALS_CAP_HEIGHT_EM,
    initialsCropBoxW: initialsCropBoxW,
    INITIALS_TEXT_MAX_WIDTH_RATIO: INITIALS_TEXT_MAX_WIDTH_RATIO,
    initialsLayout: initialsLayout,
    initialsBoxToPixels: initialsBoxToPixels,
    MRS_CAP_HEIGHT_RATIO: MRS_CAP_HEIGHT_RATIO,
    MRS_GAP_RATIO: MRS_GAP_RATIO,
    SURNAME_LETTER_HEIGHT_RATIO: SURNAME_LETTER_HEIGHT_RATIO,
    SERIF_CAP_HEIGHT_EM: SERIF_CAP_HEIGHT_EM,
    MRS_LETTER_SPACING_EM: MRS_LETTER_SPACING_EM,
    relativeLuminance: relativeLuminance,
    parsePlacement: parsePlacement,
    boxToPixels: boxToPixels,
    pathD: pathD,
    mrsPathD: mrsPathD,
    lockupMetrics: lockupMetrics,
    mrsTextStyle: mrsTextStyle,
    fontSizePx: fontSizePx,
    exactFitFontSize: exactFitFontSize,
    snapCenterX: snapCenterX,
    snapRotation: snapRotation,
    clampX: clampX,
    clampY: clampY,
    clampW: clampW,
    textStyle: textStyle
  };
})();
